/**
 * 実報酬合計を計算する
 *
 * hourly（時給）: 通常×1.0、深夜（22:00〜翌5:00）×1.25、
 *                 通常時間外（8h超）×1.25、深夜時間外×1.50
 *                 ＋ 交通費・手当
 * それ以外: salary_amount ＋ 交通費・手当
 *
 * @param salaryAmount 時給/日給/月給の金額
 * @param salaryType   'hourly' | 'daily' | 'monthly'
 * @param startTime    開始時刻 "HH:MM"
 * @param endTime      終了時刻 "HH:MM"（深夜跨ぎ対応）
 * @param breakMinutes 休憩時間（分）
 * @param salaryAllowance 手当
 * @param salaryTransport 交通費
 * @returns 合計報酬（円）。salary_amount が未設定の場合は null
 */
export const calcTotalPayment = (
  salaryAmount: number | undefined,
  salaryType: string | undefined,
  startTime: string,
  endTime: string,
  breakMinutes: number = 0,
  salaryAllowance?: string | number,
  salaryTransport?: string | number
): number | null => {
  if (!salaryAmount) return null;

  const allowance = salaryAllowance ? (Number(salaryAllowance) || 0) : 0;
  const transport = salaryTransport ? (Number(salaryTransport) || 0) : 0;

  if (!salaryType || salaryType === 'hourly') {
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    let startMin = sh * 60 + sm;
    let endMin = eh * 60 + em;
    if (endMin <= startMin) endMin += 24 * 60; // 深夜跨ぎ対応
    const totalGross = endMin - startMin;
    const workTotal = Math.max(0, totalGross - breakMinutes);
    if (workTotal === 0) return allowance + transport;

    // 深夜時間帯（22:00〜翌5:00）を絶対分で定義
    const nightRanges = [
      { s: 0, e: 5 * 60 },        // 0:00〜5:00
      { s: 22 * 60, e: 29 * 60 }, // 22:00〜翌5:00（29時間=1740分）
    ];

    // シフト期間を通常/深夜の境界で区間分割（時刻順）
    const boundaries = new Set<number>([startMin, endMin]);
    for (const nr of nightRanges) {
      if (nr.s > startMin && nr.s < endMin) boundaries.add(nr.s);
      if (nr.e > startMin && nr.e < endMin) boundaries.add(nr.e);
    }
    const sorted = Array.from(boundaries).sort((a, b) => a - b);

    // 休憩を按分比率で各区間に分配し、8時間超過を時刻順に追跡して賃金計算
    const breakRatio = totalGross > 0 ? breakMinutes / totalGross : 0;
    const OT_THRESHOLD = 8 * 60; // 480分
    let accumulated = 0;
    let pay = 0;

    for (let i = 0; i < sorted.length - 1; i++) {
      const segStart = sorted[i];
      const segEnd = sorted[i + 1];
      const gross = segEnd - segStart;
      const work = gross * (1 - breakRatio); // 按分後の実労働時間
      const mid = (segStart + segEnd) / 2;
      const isNight = nightRanges.some(nr => mid >= nr.s && mid < nr.e);
      const beforeOT = Math.max(0, OT_THRESHOLD - accumulated);
      const regular = Math.min(work, beforeOT);
      const overtime = work - regular;
      accumulated += work;

      if (isNight) {
        pay += (regular / 60) * salaryAmount * 1.25;  // 深夜基本
        pay += (overtime / 60) * salaryAmount * 1.50; // 深夜時間外
      } else {
        pay += (regular / 60) * salaryAmount * 1.00;  // 通常基本
        pay += (overtime / 60) * salaryAmount * 1.25; // 通常時間外
      }
    }

    return Math.floor(pay) + allowance + transport;
  }
  return salaryAmount + allowance + transport;
};
